/**
 * TODO: Delete when real backend is implemented
 *
 * Stand-in for the analytics backend until the real endpoints land: a deterministic
 * pipeline > workflow > sample group > task group > task hierarchy.
 *
 * Only task groups own activity, and a task group's activity is derived on demand from a
 * seed made of its token and the date rather than stored. Every level above a task group
 * is therefore exactly the sum of the task groups beneath it, and the tasks listed under
 * a task group are exactly that task group split up, so drilling down always adds back up
 * and the same query always returns the same numbers.
 */
import dayjs from 'dayjs';
import { capitalCase } from 'change-case';
import {
  TaskType,
  taskTypeZod,
} from 'packages/lib/api/annotations/contract/enums';
import {
  CostSummaryData,
  GroupType,
} from 'packages/lib/api/annotations-ops/contract';

// 2026-09-15 Used Cursor (Opus 5) to generate this temporary fixture (to be removed once the backend API is available)
// 2026-09-21 Used Cursor (Grok 4.6) to update mocks for Pipelines > Pipeline > Workflow > Sample Group > Task Group, as well as All Requests > Request ID pages
// 2026-10-08 Used Cursor (Claude Opus 5.5 Medium) to add the Task page's breadcrumb trail
// 2026-10-09 Used Cursor (Grok 4.7) to look a task token back up to the task-group row it came from

const PIPELINE_COUNT = 19;
const WORKFLOWS_PER_PIPELINE = [4, 18] as const;
const SAMPLE_GROUPS_PER_WORKFLOW = [2, 10] as const;
const TASK_GROUPS_PER_SAMPLE_GROUP = [1, 4] as const;

/** Only used to make names look like the real thing. Activity is generated for any date. */
const FIXTURE_YEAR = 2026;

/** Illustrative: task types judged per scene or object rather than per frame. */
const FRAMELESS_TASK_TYPES: TaskType[] = [
  'Attribute',
  'ImageEnrichment',
  'SceneEnrichment',
  'TrackletClassification',
];

export const hasFrames = (taskType: TaskType): boolean =>
  !FRAMELESS_TASK_TYPES.includes(taskType);

/** mulberry32 — small deterministic PRNG so the fixtures never change between renders. */
export const createRandom = (seed: number): (() => number) => {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** FNV-1a, so a given token and date always seed the same stream. */
export const seedFrom = (value: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 0x01000193);
  }
  return hash >>> 0;
};

export const money = (value: number): number => Math.round(value * 100) / 100;

export const hours = (value: number): number => Math.round(value * 10) / 10;

// ---------------------------------------------------------------------------
// Hierarchy
// ---------------------------------------------------------------------------

/** Shapes every day of work a task group does. */
interface ActivityProfile {
  /** Baseline weekday labeling spend. */
  dailyLabelingBase: number;
  qaRatio: number;
  auditRatio: number;
  scaleRatio: number;
  costPerTask: number;
  framesPerTask: number;
  annotationsPerTask: number;
  /** Turns spend into hours worked, since the fixture has no timesheets. */
  hourlyRate: number;
  /** Share of labeled tasks that also reach QA / audit on the same day. */
  qaTaskShare: number;
  auditTaskShare: number;
}

/** The leaf of the hierarchy, and the only level that owns activity. */
export interface MockTaskGroup {
  groupType: 'TaskGroup';
  token: string;
  name: string;
  taskType: TaskType;
  /**
   * Stand-in for the `managerEmail` filter. The fixture has no org chart, so any manager
   * email matches this deterministic subset rather than real direct reports.
   */
  isDirectReportWork: boolean;
  profile: ActivityProfile;
}

/** Any level above a task group; its numbers are the sum of its descendants'. */
export interface MockParentGroup {
  groupType: Exclude<GroupType, 'TaskGroup'>;
  token: string;
  name: string;
  children: MockGroup[];
  /** Present on sample groups, i.e. the request they were created for. */
  requestId?: string;
}

export type MockGroup = MockParentGroup | MockTaskGroup;

const PIPELINES: { name: string; subject: string; abbreviation: string }[] = [
  {
    name: 'TrafficLight',
    subject: 'Traffic Light Detection',
    abbreviation: 'TLD',
  },
  { name: 'Image', subject: 'Image Enrichment', abbreviation: 'IE' },
  { name: 'Fusion', subject: 'Sensor Fusion', abbreviation: 'SF' },
  {
    name: 'GTRegression',
    subject: 'Ground Truth Regression',
    abbreviation: 'GTR',
  },
  {
    name: 'FusionCloudFactory',
    subject: 'Fusion Cloud Factory',
    abbreviation: 'FCF',
  },
  { name: 'LaneGraph', subject: 'Lane Graph Extraction', abbreviation: 'LGE' },
  {
    name: 'PedestrianIntent',
    subject: 'Pedestrian Intent',
    abbreviation: 'PI',
  },
  { name: 'RoadSigns', subject: 'Road Sign Recognition', abbreviation: 'RSR' },
  { name: 'FreeSpace', subject: 'Free Space Estimation', abbreviation: 'FSE' },
  { name: 'OccupancyGrid', subject: 'Occupancy Grid', abbreviation: 'OG' },
  { name: 'LidarSeg', subject: 'Lidar Segmentation', abbreviation: 'LS' },
  {
    name: 'RadarTracks',
    subject: 'Radar Track Association',
    abbreviation: 'RTA',
  },
  {
    name: 'Localization',
    subject: 'Localization Landmarks',
    abbreviation: 'LL',
  },
  { name: 'MapUpdate', subject: 'Map Update Review', abbreviation: 'MUR' },
  {
    name: 'BehaviorPrediction',
    subject: 'Behavior Prediction',
    abbreviation: 'BP',
  },
  {
    name: 'CyclistDetection',
    subject: 'Cyclist Detection',
    abbreviation: 'CD',
  },
  {
    name: 'ConstructionZones',
    subject: 'Construction Zone Mapping',
    abbreviation: 'CZM',
  },
  {
    name: 'NightDriving',
    subject: 'Night Driving Scenes',
    abbreviation: 'NDS',
  },
  {
    name: 'AdverseWeather',
    subject: 'Adverse Weather Scenes',
    abbreviation: 'AWS',
  },
];

const MODALITIES = ['Video', 'Lidar', 'Image', 'Multiview', 'Audio'] as const;

const WORKFLOW_QUALIFIERS = [
  '',
  'for DTBs ',
  'for Rail Road Lights ',
  'for Non-Functional TLs ',
  'for Flashing Red ',
  'for Pedestrian Warning Beacons ',
  'for Occluded Views ',
  'for Tunnels ',
] as const;

const SAMPLE_GROUP_SUBJECTS = [
  'Driverless-route-missing',
  'Route-replay-gap',
  'Heavy-rain-segment',
  'Dusk-glare-segment',
  'Unprotected-left',
  'Double-parked-vehicle',
  'School-zone-pass',
  'Merge-conflict',
] as const;

const SAMPLE_GROUP_SUFFIXES = ['MAP', 'SIM', 'FLEET', 'REPLAY'] as const;

/** Every tree-building decision draws from this one stream, so the tree never changes. */
const random = createRandom(20260915);

const pick = <T>(values: readonly T[]): T =>
  values[Math.floor(random() * values.length)];

const between = (min: number, max: number): number =>
  min + random() * (max - min);

const intBetween = ([min, max]: readonly [number, number]): number =>
  min + Math.floor(random() * (max - min + 1));

const hexToken = (): string =>
  Array.from({ length: 32 }, () => Math.floor(random() * 16).toString(16)).join(
    ''
  );

const createProfile = (taskType: TaskType): ActivityProfile => ({
  // Annual labeling for a task group lands in the low hundreds to tens of thousands;
  // spread across ~220 active weekdays that comes out to a few dollars to ~$100/day.
  dailyLabelingBase: between(800, 28000) / 220,
  qaRatio: between(0.15, 0.4),
  auditRatio: between(0.1, 0.32),
  // ~50/50 whether iMerit labeling beats Scale or costs more.
  scaleRatio: random() < 0.5 ? between(0.55, 0.95) : between(1.1, 2.1),
  costPerTask: between(1.5, 14),
  framesPerTask: hasFrames(taskType) ? Math.round(between(1, 6)) : 0,
  annotationsPerTask: Math.round(between(2, 40)),
  hourlyRate: between(8, 22),
  qaTaskShare: between(0.3, 0.9),
  auditTaskShare: between(0.1, 0.5),
});

const createTaskGroup = (
  workflowName: string,
  taskType: TaskType
): MockTaskGroup => ({
  groupType: 'TaskGroup',
  token: hexToken(),
  name: `${workflowName} ${capitalCase(taskType)} Task Group`,
  taskType,
  isDirectReportWork: random() < 0.4,
  profile: createProfile(taskType),
});

const createSampleGroup = (
  workflowName: string,
  abbreviation: string
): MockParentGroup => {
  const request = `REQ${intBetween([1, 9])}.Q${intBetween([1, 4])}${
    FIXTURE_YEAR % 100
  }`;
  const date = `${String(intBetween([1, 12])).padStart(2, '0')}.${String(
    intBetween([1, 28])
  ).padStart(2, '0')}.${FIXTURE_YEAR}`;
  const visibility = random() < 0.7 ? 'internal' : 'external';
  const name = `[${request}]${abbreviation}-${pick(
    SAMPLE_GROUP_SUBJECTS
  )}[${date}][${visibility}]_${pick(SAMPLE_GROUP_SUFFIXES)}_part${intBetween([
    1, 6,
  ])}`;

  // A sample group's task groups are different stages of the same work, so their types differ.
  const taskTypes = new Set<TaskType>();
  const taskGroupCount = intBetween(TASK_GROUPS_PER_SAMPLE_GROUP);
  while (taskTypes.size < taskGroupCount)
    taskTypes.add(pick(taskTypeZod.options));

  return {
    groupType: 'SampleGroup',
    token: hexToken(),
    name,
    requestId: request,
    children: [...taskTypes].map((taskType) =>
      createTaskGroup(workflowName, taskType)
    ),
  };
};

const createWorkflow = (
  subject: string,
  abbreviation: string
): MockParentGroup => {
  const name = `${FIXTURE_YEAR} ${subject} (${pick(MODALITIES)}) ${pick(
    WORKFLOW_QUALIFIERS
  )}v${intBetween([1, 3])}`;

  return {
    groupType: 'Workflow',
    token: hexToken(),
    name,
    children: Array.from(
      { length: intBetween(SAMPLE_GROUPS_PER_WORKFLOW) },
      () => createSampleGroup(name, abbreviation)
    ),
  };
};

const createPipeline = ({
  name,
  subject,
  abbreviation,
}: (typeof PIPELINES)[number]): MockParentGroup => ({
  groupType: 'Pipeline',
  token: hexToken(),
  name,
  children: Array.from({ length: intBetween(WORKFLOWS_PER_PIPELINE) }, () =>
    createWorkflow(subject, abbreviation)
  ),
});

/** The All Pipelines view, i.e. every pipeline aggregated together. */
const ROOT: MockParentGroup = {
  groupType: 'AllPipelines',
  token: '',
  name: 'All Pipelines',
  children: PIPELINES.slice(0, PIPELINE_COUNT).map(createPipeline),
};

const GROUP_BY_TOKEN = new Map<string, MockGroup>();
/** Insertion order is the index encoded in each task token. */
const TASK_GROUPS: MockTaskGroup[] = [];
const TASK_GROUP_INDEX = new Map<string, number>();

/** Every task group, in the order its token index was assigned. */
export const taskGroups = (): readonly MockTaskGroup[] => TASK_GROUPS;
/** Pipeline down to the group itself, i.e. what the hierarchy endpoint returns. */
const CHAIN_BY_TOKEN = new Map<string, MockGroup[]>();
const SAMPLE_GROUPS_BY_REQUEST_ID = new Map<string, MockParentGroup[]>();

const indexGroup = (group: MockGroup, ancestors: MockGroup[]): void => {
  const chain = [...ancestors, group];
  GROUP_BY_TOKEN.set(group.token, group);
  CHAIN_BY_TOKEN.set(group.token, chain);
  if (group.groupType === 'SampleGroup' && group.requestId) {
    const sampleGroups = SAMPLE_GROUPS_BY_REQUEST_ID.get(group.requestId) ?? [];
    sampleGroups.push(group);
    SAMPLE_GROUPS_BY_REQUEST_ID.set(group.requestId, sampleGroups);
  }
  if (group.groupType === 'TaskGroup') {
    TASK_GROUP_INDEX.set(group.token, TASK_GROUPS.length);
    TASK_GROUPS.push(group);
  } else {
    group.children.forEach((child) => indexGroup(child, chain));
  }
};

ROOT.children.forEach((pipeline) => indexGroup(pipeline, []));

const REQUEST_BY_ID = new Map<string, MockParentGroup>(
  [...SAMPLE_GROUPS_BY_REQUEST_ID.entries()].map(([requestId, children]) => [
    requestId,
    {
      groupType: 'RequestId',
      token: requestId,
      name: requestId,
      children,
    },
  ])
);

/** All Requests: one child per request ID, each of which contains that request's sample groups. */
const ALL_REQUESTS: MockParentGroup = {
  groupType: 'AllRequests',
  token: '',
  name: 'All Requests',
  children: [...REQUEST_BY_ID.values()],
};

export const findGroup = (
  groupType: GroupType,
  token?: string
): MockGroup | undefined => {
  if (groupType === 'AllPipelines') return ROOT;
  if (groupType === 'AllRequests') return ALL_REQUESTS;
  if (groupType === 'RequestId') {
    return token ? REQUEST_BY_ID.get(token) : undefined;
  }
  const group = token ? GROUP_BY_TOKEN.get(token) : undefined;
  return group?.groupType === groupType ? group : undefined;
};

/** All Pipelines is the implicit first breadcrumb, so the root is not part of the chain. */
export const groupChain = (token: string): MockGroup[] =>
  CHAIN_BY_TOKEN.get(token) ?? [];

export const taskGroupsUnder = (group: MockGroup): MockTaskGroup[] =>
  group.groupType === 'TaskGroup'
    ? [group]
    : group.children.flatMap(taskGroupsUnder);

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

/** One day of completed work in a task group. */
export interface DayActivity {
  numTasks: number;
  numQATasks: number;
  numAuditTasks: number;
  numFrames: number;
  numAnnotations: number;
  labelingCost: number;
  qaCost: number;
  auditCost: number;
  scaleCost: number;
  labelingHours: number;
  qaHours: number;
  auditHours: number;
}

/** One calendar day of a query's range, resolved once rather than per task group. */
export interface RangeDay {
  /** YYYY-MM-DD */
  date: string;
  isWeekend: boolean;
  /**
   * How busy the whole organization was that day. Without a rhythm every group shares,
   * averaging thousands of task groups would flatten the chart on the higher levels.
   */
  activityFactor: number;
}

export const datesInRange = (
  startDate: string,
  endDate: string
): RangeDay[] => {
  const start = dayjs(startDate);
  const dayCount = Math.max(0, dayjs(endDate).diff(start, 'day') + 1);
  return Array.from({ length: dayCount }, (_, index) => {
    const day = start.add(index, 'day');
    const date = day.format('YYYY-MM-DD');
    const weekday = day.day();
    return {
      date,
      isWeekend: weekday === 0 || weekday === 6,
      activityFactor: 0.7 + createRandom(seedFrom(`day:${date}`))() * 0.6,
    };
  });
};

export const emptyDay = (): DayActivity => ({
  numTasks: 0,
  numQATasks: 0,
  numAuditTasks: 0,
  numFrames: 0,
  numAnnotations: 0,
  labelingCost: 0,
  qaCost: 0,
  auditCost: 0,
  scaleCost: 0,
  labelingHours: 0,
  qaHours: 0,
  auditHours: 0,
});

/**
 * What one task group completed on one day. Weekends are quieter and some weekdays are
 * idle, so short date ranges legitimately drop groups that did no work.
 */
export const dayActivity = (
  { token, profile }: MockTaskGroup,
  { date, isWeekend, activityFactor }: RangeDay
): DayActivity => {
  const dayRandom = createRandom(seedFrom(`${token}:${date}`));
  const activeChance = isWeekend ? 0.25 : 0.85;
  if (dayRandom() > activeChance) return emptyDay();

  const intensity = isWeekend
    ? 0.2 + dayRandom() * 0.4
    : 0.5 + dayRandom() * 0.9;
  const labelingCost = money(
    profile.dailyLabelingBase * intensity * activityFactor
  );
  const qaCost = money(labelingCost * profile.qaRatio);
  const auditCost = money(labelingCost * profile.auditRatio);
  const numTasks = Math.max(1, Math.round(labelingCost / profile.costPerTask));

  return {
    numTasks,
    numQATasks: Math.round(numTasks * profile.qaTaskShare),
    numAuditTasks: Math.round(numTasks * profile.auditTaskShare),
    numFrames: numTasks * profile.framesPerTask,
    numAnnotations: numTasks * profile.annotationsPerTask,
    labelingCost,
    qaCost,
    auditCost,
    scaleCost: money(labelingCost * profile.scaleRatio),
    labelingHours: hours(labelingCost / profile.hourlyRate),
    qaHours: hours(qaCost / profile.hourlyRate),
    auditHours: hours(auditCost / profile.hourlyRate),
  };
};

/** Every cost endpoint's `summary` is the same reduction of its activity. */
export const summaryFromActivity = (totals: DayActivity): CostSummaryData => {
  const labelingCost = money(totals.labelingCost);
  const qaCost = money(totals.qaCost);
  const auditCost = money(totals.auditCost);

  return {
    labelingCost,
    industryCost: money(totals.scaleCost),
    qaCost,
    auditCost,
    numTasks: totals.numTasks,
    numFrames: totals.numFrames,
    numAnnotations: totals.numAnnotations,
    totalCost: money(labelingCost + qaCost + auditCost),
  };
};

export const addDay = (totals: DayActivity, day: DayActivity): void => {
  totals.numTasks += day.numTasks;
  totals.numQATasks += day.numQATasks;
  totals.numAuditTasks += day.numAuditTasks;
  totals.numFrames += day.numFrames;
  totals.numAnnotations += day.numAnnotations;
  totals.labelingCost += day.labelingCost;
  totals.qaCost += day.qaCost;
  totals.auditCost += day.auditCost;
  totals.scaleCost += day.scaleCost;
  totals.labelingHours += day.labelingHours;
  totals.qaHours += day.qaHours;
  totals.auditHours += day.auditHours;
};

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

/** One completed task, i.e. one row of the Task Group page. */
export interface MockTask {
  id: string;
  numFrames: number;
  numAnnotations: number;
  labelingCost: number;
  qaCost: number;
  auditCost: number;
  scaleCost: number;
}

/**
 * Splits `total` into parts weighted by `weights`. The last part absorbs the rounding
 * remainder so the parts always add back up to `total` exactly.
 */
export const splitTotal = (
  total: number,
  weights: number[],
  round: (value: number) => number
): number[] => {
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  let remaining = total;
  return weights.map((weight, index) => {
    if (index === weights.length - 1) return round(remaining);
    const part = round((total * weight) / weightTotal);
    remaining -= part;
    return part;
  });
};

const TASK_TOKEN_EPOCH = '2020-01-01';

/** Group index, day, and task index, in the shape `taskTokenFrom` writes. */
const TASK_TOKEN_PATTERN =
  /^([0-9a-f]{8})-([0-9a-f]{4})-4[0-9a-f]{3}-8000-000000000000$/i;

/**
 * A UUID whose first digits are the task group's index, so the hierarchy mock can walk
 * back up from a task token. The rest records the day and the task's place within it.
 */
export const taskTokenFrom = (
  taskGroup: MockTaskGroup,
  date: string,
  index: number
): string => {
  const groupIndex = TASK_GROUP_INDEX.get(taskGroup.token) ?? 0;
  const day = dayjs(date).diff(TASK_TOKEN_EPOCH, 'day');
  const groupHex = groupIndex.toString(16).padStart(8, '0');
  const dayHex = Math.max(0, day).toString(16).padStart(4, '0');
  const indexHex = index.toString(16).padStart(3, '0');
  return `${groupHex}-${dayHex}-4${indexHex}-8000-000000000000`;
};

/** The task group a mock task token belongs to, or nothing for any other UUID. */
export const taskGroupForTask = (taskId: string): MockTaskGroup | undefined => {
  const match = TASK_TOKEN_PATTERN.exec(taskId);
  if (!match) return undefined;
  return TASK_GROUPS[Number.parseInt(match[1], 16)];
};

/** The day encoded in a mock task token, which is the day its labeling started. */
export const taskDateFromToken = (taskId: string): string | undefined => {
  const match = TASK_TOKEN_PATTERN.exec(taskId);
  if (!match) return undefined;
  return dayjs(TASK_TOKEN_EPOCH)
    .add(Number.parseInt(match[2], 16), 'day')
    .format('YYYY-MM-DD');
};

/**
 * The individual tasks behind one day of a task group. They share a single set of
 * weights, so a task that did more labeling also carries more frames and annotations.
 */
export const tasksForDay = (
  taskGroup: MockTaskGroup,
  { date }: RangeDay,
  day: DayActivity
): MockTask[] => {
  const taskRandom = createRandom(seedFrom(`${taskGroup.token}:${date}:tasks`));
  const weights = Array.from(
    { length: day.numTasks },
    () => 0.5 + taskRandom()
  );

  const labeling = splitTotal(day.labelingCost, weights, money);
  const qa = splitTotal(day.qaCost, weights, money);
  const audit = splitTotal(day.auditCost, weights, money);
  const scale = splitTotal(day.scaleCost, weights, money);
  const frames = splitTotal(day.numFrames, weights, Math.round);
  const annotations = splitTotal(day.numAnnotations, weights, Math.round);

  return weights.map((_, index) => ({
    id: taskTokenFrom(taskGroup, date, index),
    numFrames: frames[index],
    numAnnotations: annotations[index],
    labelingCost: labeling[index],
    qaCost: qa[index],
    auditCost: audit[index],
    scaleCost: scale[index],
  }));
};

/**
 * The task-group row this token was split from. Frames, annotations, and industry cost
 * are taken from it. A pass's own cost is hours times the hourly rate.
 */
export const taskForToken = (taskId: string): MockTask | undefined => {
  const taskGroup = taskGroupForTask(taskId);
  const date = taskDateFromToken(taskId);
  if (!taskGroup || !date) return undefined;
  const [day] = datesInRange(date, date);
  if (!day) return undefined;
  return tasksForDay(taskGroup, day, dayActivity(taskGroup, day)).find(
    (task) => task.id === taskId
  );
};
